import React from 'react';
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import { motion } from 'framer-motion';
import { Plane, Hotel, Bus, Utensils, Phone, Stamp, ChevronRight, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { SERVICES, COMPANY } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { sacredRise, imageReveal } from '../lib/animations';

const iconMap = { Plane, Hotel, Bus, Utensils, Phone, Stamp, Globe, Building: Hotel };
const serviceImages = {
  '01': 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=800&h=600&fit=crop&q=80',
  '02': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop&q=80',
  '03': 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&h=600&fit=crop&q=80',
  '04': 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&h=600&fit=crop&q=80',
  '05': 'https://images.unsplash.com/photo-1556745753-b2904692b3cd?w=800&h=600&fit=crop&q=80',
  '06': 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&h=600&fit=crop&q=80',
  '07': 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&h=600&fit=crop&q=80',
};

export default function Services() {
  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`Our Services: Visas, Hotels & Transport | ${brandConfig.companyName}`}
        pageDescription={`${brandConfig.companyName} provides complete travel solutions including fast Umrah visa processing, premium hotel bookings in Saudi Arabia, and global flight tickets.`}
        pageUrl="/services"
        faqItems={ENTITY.pageFAQ?.services}
      />
      <section style={{ position: 'relative', minHeight: '50vh', overflow: 'hidden', background: '#0A1628', display: 'flex', alignItems: 'center' }}>
        <img src="https://images.unsplash.com/photo-1574246604907-db69e30ddb97?w=1920&h=600&fit=crop&q=80" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.85)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.05} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '120px var(--gutter) 60px' }}>
          <div className="arabic-text" style={{ fontSize: 18, color: '#C9A84C' }}>{COMPANY.bismillah}</div>
          <h1 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FFFFFF', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            Our Services.<br /><span style={{ color: '#C9A84C' }}>Crafted with Care.</span>
          </h1>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', maxWidth: 560, marginTop: 16 }}>
            Every detail of your sacred journey — from visa to meals — handled with experience and reverence.
          </p>
        </div>
      </section>

      <section style={{ background: '#FBF8F0', padding: 'var(--section) 0' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          {SERVICES.map((s, i) => {
            const Icon = iconMap[s.icon] || Plane;
            const reversed = i % 2 === 1;
            return (
              <motion.div key={s.id} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
                variants={sacredRise} className="svc-row"
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60,
                  alignItems: 'center', marginBottom: 100,
                  direction: reversed ? 'rtl' : 'ltr',
                }}>
                <div style={{ direction: 'ltr', position: 'relative' }}>
                  <motion.img src={serviceImages[s.id]} alt={s.name} variants={imageReveal}
                    style={{ width: '100%', height: 420, objectFit: 'cover' }} />
                  <div style={{
                    position: 'absolute', bottom: 20, left: 20,
                    background: 'rgba(10,22,40,0.95)', borderLeft: '3px solid #C9A84C',
                    padding: '14px 20px',
                  }}>
                    <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#C9A84C', fontSize: 14, letterSpacing: '0.15em' }}>
                      {s.tag}
                    </div>
                  </div>
                </div>
                <div style={{ direction: 'ltr' }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%', background: '#1B6B3A',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(201,168,76,0.4)',
                  }}>
                    <Icon size={28} color="#C9A84C" strokeWidth={1.5} />
                  </div>
                  <div style={{ fontFamily: 'Inter', fontWeight: 600, color: '#C9A84C', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', marginTop: 20 }}>
                    {s.id} / Service
                  </div>
                  <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 'var(--h2)', marginTop: 10, lineHeight: 1.1 }}>
                    {s.name}
                  </h2>
                  <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 20, marginTop: 8 }}>
                    {s.arabic}
                  </div>
                  <p style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 'var(--body-lg)', lineHeight: 1.85, marginTop: 18 }}>
                    {s.desc}
                  </p>
                  <Link to="/contact" style={{ textDecoration: 'none' }}>
                    <button style={{
                      marginTop: 24, padding: '12px 28px', background: 'transparent',
                      border: '2px solid #1B6B3A', color: '#1B6B3A', fontFamily: 'Inter',
                      fontWeight: 600, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}>
                      Inquire Now <ChevronRight size={14} />
                    </button>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .svc-row { grid-template-columns: 1fr !important; gap: 32px !important; direction: ltr !important; margin-bottom: 60px !important; }
          .svc-row img { height: 280px !important; }
        }
      `}</style>
    </PageWrapper>
  );
}
